/**
 * The server-side browser-action tool family (GptAssistant BrowserToolNames). Each
 * is an opt-in built-in tool that only exists on runs made from inside the Chrome
 * extension; its call is relayed by hooks/useBrowserActionRelay and rendered by
 * components/responses/BrowserActionCallOutput.
 */
export const BROWSER_ACTION_TOOL_NAMES = [
    'browser_snapshot',
    'browser_act',
    'browser_navigate',
    'browser_tabs',
    'browser_screenshot'
] as const;

export type BrowserActionToolName = (typeof BROWSER_ACTION_TOOL_NAMES)[number];

export const isBrowserActionToolName = (
    name: unknown
): name is BrowserActionToolName =>
    typeof name === 'string' &&
    (BROWSER_ACTION_TOOL_NAMES as readonly string[]).includes(name);

/** Arguments the model passes; only the fields the UI reads are typed. */
export interface BrowserActionCallArguments {
    action?: string;
    reason?: string;
    url?: string;
    [key: string]: unknown;
}

const humanizeToken = (value: string): string => value.replace(/_/g, ' ');

/**
 * Short label for a call card: "Browser: click", "Browser: snapshot",
 * "Browser: navigate back", "Browser: tabs list".
 */
export const describeBrowserActionCall = (
    toolName: string,
    args: BrowserActionCallArguments
): string => {
    const action =
        typeof args.action === 'string' && args.action.trim()
            ? humanizeToken(args.action.trim())
            : undefined;
    switch (toolName) {
        case 'browser_snapshot':
            return 'Browser: snapshot';
        case 'browser_screenshot':
            return 'Browser: screenshot';
        case 'browser_act':
            return action ? `Browser: ${action}` : 'Browser: action';
        case 'browser_navigate':
            return action && action !== 'url'
                ? `Browser: navigate ${action}`
                : 'Browser: navigate';
        case 'browser_tabs':
            return action ? `Browser: tabs ${action}` : 'Browser: tabs';
        default:
            return `Browser: ${humanizeToken(toolName.replace(/^browser_/, ''))}`;
    }
};
